import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { useSettings } from "../../contexts/SettingsContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
    AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Plus, Trash2, Save, RotateCcw, ArrowUp, ArrowDown, Eye, ExternalLink } from "lucide-react";
import { toast, Toaster } from "sonner";

/**
 * Admin → Legal pages editor.
 *
 * Four tabs (Terms / Privacy / Refund / Shipping). Each tab is a section
 * editor — add / move / delete / edit title + Markdown body — with a live
 * preview panel using the same renderer the public page uses.
 *
 * Placeholders {{brand}} / {{brand_full}} / {{email}} are documented at the top
 * of the body field. The admin can use them freely; they expand against the
 * live Settings doc on render.
 */
const SLUGS = [
    { slug: "terms", label: "Terms & Conditions", testid: "tab-legal-terms" },
    { slug: "privacy", label: "Privacy Policy", testid: "tab-legal-privacy" },
    { slug: "refund", label: "Refund & Cancellation", testid: "tab-legal-refund" },
    { slug: "shipping", label: "Shipping Policy", testid: "tab-legal-shipping" },
];

export default function AdminLegalPages() {
    const [active, setActive] = useState("terms");

    return (
        <div data-testid="admin-legal-pages">
            <Toaster position="top-center" />
            <div className="flex items-start justify-between mb-6 flex-wrap gap-4">
                <div>
                    <div className="label-eyebrow text-sky-700">Site content</div>
                    <h1 className="font-serif text-4xl text-stone-900 mt-2">Legal pages</h1>
                    <p className="text-sm text-stone-500 mt-1 max-w-xl">
                        Edit your Terms, Privacy, Refund and Shipping pages without a redeploy.
                        Add or remove sections, reorder them, or reseed to the original defaults.
                        Live preview on the right matches what customers see.
                    </p>
                </div>
            </div>

            <Tabs value={active} onValueChange={setActive}>
                <TabsList className="rounded-none flex flex-wrap h-auto">
                    {SLUGS.map((s) => (
                        <TabsTrigger key={s.slug} value={s.slug} className="rounded-none data-[state=active]:bg-sky-700 data-[state=active]:text-white" data-testid={s.testid}>
                            {s.label}
                        </TabsTrigger>
                    ))}
                </TabsList>
                {SLUGS.map((s) => (
                    <TabsContent key={s.slug} value={s.slug} className="mt-4">
                        <LegalPageEditor slug={s.slug} />
                    </TabsContent>
                ))}
            </Tabs>
        </div>
    );
}

function LegalPageEditor({ slug }) {
    const [page, setPage] = useState(null);
    const [draft, setDraft] = useState(null);
    const [saving, setSaving] = useState(false);
    const [reseeding, setReseeding] = useState(false);
    const [confirmReseed, setConfirmReseed] = useState(false);
    const [previewOpen, setPreviewOpen] = useState(true);

    const load = async () => {
        try {
            const { data } = await api.get(`/admin/legal/${slug}`);
            setPage(data);
            setDraft(JSON.parse(JSON.stringify(data)));
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load page");
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

    const dirty = useMemo(() => {
        if (!page || !draft) return false;
        return JSON.stringify({ t: page.title, l: page.last_updated_label, s: page.sections })
            !== JSON.stringify({ t: draft.title, l: draft.last_updated_label, s: draft.sections });
    }, [page, draft]);

    const save = async () => {
        if (!draft.sections?.length) {
            toast.error("A legal page needs at least one section");
            return;
        }
        for (const [i, s] of draft.sections.entries()) {
            if (!s.title?.trim()) {
                toast.error(`Section ${i + 1} needs a title`);
                return;
            }
        }
        setSaving(true);
        try {
            const { data } = await api.put(`/admin/legal/${slug}`, {
                title: draft.title,
                last_updated_label: draft.last_updated_label,
                sections: draft.sections,
            });
            setPage(data);
            setDraft(JSON.parse(JSON.stringify(data)));
            toast.success("Saved · live site updated");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const reseed = async () => {
        setReseeding(true);
        try {
            const { data } = await api.post(`/admin/legal/${slug}/seed`);
            setPage(data);
            setDraft(JSON.parse(JSON.stringify(data)));
            toast.success("Reset to default content");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally {
            setReseeding(false);
            setConfirmReseed(false);
        }
    };

    const addSection = (atIndex = null) => {
        const at = atIndex == null ? draft.sections.length : atIndex + 1;
        const next = [...draft.sections];
        const n = next.length + 1;
        next.splice(at, 0, { title: `${n}. New section`, body_md: "" });
        setDraft({ ...draft, sections: next });
    };
    const removeSection = (i) => {
        if (!confirm(`Remove section "${draft.sections[i]?.title}"?`)) return;
        setDraft({ ...draft, sections: draft.sections.filter((_, idx) => idx !== i) });
    };
    const moveSection = (i, dir) => {
        const j = i + dir;
        if (j < 0 || j >= draft.sections.length) return;
        const next = [...draft.sections];
        [next[i], next[j]] = [next[j], next[i]];
        setDraft({ ...draft, sections: next });
    };
    const updateSection = (i, patch) => {
        setDraft({
            ...draft,
            sections: draft.sections.map((s, idx) => idx === i ? { ...s, ...patch } : s),
        });
    };

    if (!draft) return <div className="text-sm text-stone-500 py-12 text-center" data-testid="legal-editor-loading">Loading…</div>;

    return (
        <div className="grid lg:grid-cols-5 gap-6" data-testid={`legal-editor-${slug}`}>
            {/* ----- LEFT: editor ----- */}
            <div className={previewOpen ? "lg:col-span-3 space-y-4" : "lg:col-span-5 space-y-4"}>
                {/* Title + last-updated + actions */}
                <div className="bg-white border border-stone-200 p-4 space-y-3">
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">Page title</Label>
                        <Input
                            value={draft.title}
                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                            className="rounded-none border-stone-300 mt-1"
                            data-testid="legal-title"
                        />
                    </div>
                    <div>
                        <Label className="text-[10px] uppercase tracking-widest text-stone-500">"Last updated" sub-line</Label>
                        <Input
                            value={draft.last_updated_label || ""}
                            onChange={(e) => setDraft({ ...draft, last_updated_label: e.target.value })}
                            placeholder="Last updated: 14 May 2026"
                            className="rounded-none border-stone-300 mt-1"
                            data-testid="legal-updated"
                        />
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                        <Button onClick={save} disabled={!dirty || saving} className="rounded-none bg-sky-700 hover:bg-sky-800 text-stone-50" data-testid="legal-save">
                            <Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : (dirty ? "Save changes" : "Saved")}
                        </Button>
                        <Button onClick={() => setConfirmReseed(true)} variant="outline" className="rounded-none border-stone-300" data-testid="legal-reseed">
                            <RotateCcw className="w-4 h-4 mr-2" />Reset to default
                        </Button>
                        <Button onClick={() => setPreviewOpen((v) => !v)} variant="ghost" className="rounded-none text-sky-700" data-testid="legal-toggle-preview">
                            <Eye className="w-4 h-4 mr-2" />{previewOpen ? "Hide preview" : "Show preview"}
                        </Button>
                        <a
                            href={`/${slug === "terms" ? "terms" : slug === "privacy" ? "privacy" : slug === "refund" ? "refund-policy" : "shipping-policy"}`}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center text-xs text-stone-500 hover:text-sky-700 ml-auto"
                            data-testid="legal-open-public"
                        >
                            <ExternalLink className="w-3.5 h-3.5 mr-1" />Open live page
                        </a>
                    </div>
                    <div className="bg-sky-50 border border-sky-100 p-3 text-[11px] text-sky-900">
                        Tip: use <code>{"{{brand}}"}</code>, <code>{"{{brand_full}}"}</code> and <code>{"{{email}}"}</code> in
                        any section — they'll be replaced with your live brand name + contact email at render. Markdown supported:
                        <code className="ml-1">**bold**</code>, <code>*italic*</code>, <code>`code`</code>, bullet lists with <code>-</code> or <code>•</code>.
                    </div>
                </div>

                {/* Sections list */}
                <div className="space-y-2">
                    {(draft.sections || []).map((s, i) => (
                        <div key={i} className="bg-white border border-stone-200 p-3 space-y-2" data-testid={`legal-section-${i}`}>
                            <div className="flex items-center gap-2">
                                <div className="text-[10px] uppercase tracking-widest text-stone-400 font-mono w-8">#{i + 1}</div>
                                <Input
                                    value={s.title}
                                    onChange={(e) => updateSection(i, { title: e.target.value })}
                                    placeholder="Section title"
                                    className="rounded-none border-stone-300 flex-1 font-serif text-lg"
                                    data-testid={`legal-section-title-${i}`}
                                />
                                <Button variant="ghost" size="icon" onClick={() => moveSection(i, -1)} disabled={i === 0} className="rounded-none" data-testid={`legal-section-up-${i}`}><ArrowUp className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => moveSection(i, 1)} disabled={i === draft.sections.length - 1} className="rounded-none" data-testid={`legal-section-down-${i}`}><ArrowDown className="w-4 h-4" /></Button>
                                <Button variant="ghost" size="icon" onClick={() => removeSection(i)} className="rounded-none text-rose-700" data-testid={`legal-section-del-${i}`}><Trash2 className="w-4 h-4" /></Button>
                            </div>
                            <Textarea
                                value={s.body_md}
                                onChange={(e) => updateSection(i, { body_md: e.target.value })}
                                placeholder="Section body (Markdown). Use blank lines to separate paragraphs."
                                rows={6}
                                className="rounded-none border-stone-300 font-mono text-xs"
                                data-testid={`legal-section-body-${i}`}
                            />
                            <div className="flex justify-end">
                                <Button variant="ghost" size="sm" onClick={() => addSection(i)} className="rounded-none text-sky-700 text-xs" data-testid={`legal-section-insert-${i}`}>
                                    <Plus className="w-3.5 h-3.5 mr-1" />Insert section below
                                </Button>
                            </div>
                        </div>
                    ))}
                    <Button onClick={() => addSection()} variant="outline" className="rounded-none border-sky-300 text-sky-700 w-full" data-testid="legal-section-add">
                        <Plus className="w-4 h-4 mr-2" />Add new section at the end
                    </Button>
                </div>
            </div>

            {/* ----- RIGHT: live preview ----- */}
            {previewOpen && (
                <div className="lg:col-span-2">
                    <div className="lg:sticky lg:top-4">
                        <div className="bg-white border border-stone-200 max-h-[80vh] overflow-y-auto">
                            <div className="px-4 py-2 border-b border-stone-100 bg-stone-50 flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-widest text-stone-500">Live preview</span>
                                <span className="text-[10px] text-stone-400">Updates as you type</span>
                            </div>
                            <div className="p-2">
                                <InlinePreview slug={slug} draft={draft} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog open={confirmReseed} onOpenChange={(o) => !o && setConfirmReseed(false)}>
                <AlertDialogContent className="rounded-none">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Reset {SLUGS.find((s) => s.slug === slug)?.label} to default?</AlertDialogTitle>
                        <AlertDialogDescription className="text-sm text-stone-600">
                            All sections will be replaced with the original Hahnemann seed content. Any custom wording or
                            sections you've added will be lost. This cannot be undone — but you can always re-edit afterwards.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none">Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={reseed} disabled={reseeding} className="rounded-none bg-rose-700 hover:bg-rose-800 text-white" data-testid="legal-reseed-confirm">
                            {reseeding ? "Resetting…" : "Reset"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/**
 * Renders the in-memory draft using a minimal inline Markdown subset
 * (avoids a network round-trip per keystroke). The public LegalPages.jsx
 * is the source of truth — keep the renderers visually compatible.
 */
function InlinePreview({ slug, draft }) {
    // Re-implements the public LegalPage renderer minimally so the preview
    // doesn't need a network round-trip per keystroke. The renderer behavior
    // mirrors LegalPages.jsx (placeholder expansion only — the public page
    // applies the full Markdown subset on top). Reactive to live Settings.
    const { settings } = useSettings();
    const vars = {
        brand: settings?.brand_name || "Hahnemann",
        brandFull: settings?.brand_subtitle_full || "Pharmacy & Laboratory",
        email: settings?.contact_email || "help@hplmedicines.com",
    };
    const expand = (t) => (t || "")
        .replace(/\{\{brand\}\}/g, vars.brand)
        .replace(/\{\{brand_full\}\}/g, vars.brandFull)
        .replace(/\{\{email\}\}/g, vars.email);
    return (
        <div className="px-4 py-3" data-testid={`legal-preview-${slug}`}>
            <div className="label-eyebrow text-sky-700">Legal</div>
            <h1 className="font-serif text-2xl text-slate-900 mt-2 mb-3">{expand(draft.title)}</h1>
            <p className="text-[10px] text-stone-500 mb-5">{draft.last_updated_label || ""}</p>
            {(draft.sections || []).map((s, i) => (
                <section key={i} className="mb-5">
                    <h2 className="font-serif text-lg text-slate-900 mb-1.5">{expand(s.title)}</h2>
                    <div className="text-xs text-stone-700 leading-relaxed whitespace-pre-wrap">{expand(s.body_md)}</div>
                </section>
            ))}
        </div>
    );
}
